// Live Core cards on the page (shared/API.md §40): the in-memory cache (nulls included) and the
// batched asker. A row asks when its card slot is DRAWN — on screen (IntersectionObserver), not
// merely mounted — and again only when its cached answer is older than its TTL; never on a timer.
// Requests are batched over ~150 ms (≤ 20 URLs). A 401 (no Core account) or a missing proxy route
// stops asking for this session; a failure keeps the last card.
import { useEffect, useRef, useSyncExternalStore, type RefObject } from 'react';
import { data } from '../../../bridge/client';
import { CORE_DEBOUNCE_MS, CoreLinkCards, type CacheEntry, type LinkCard } from '../../../core/coreLinkCards';

let cache: ReadonlyMap<string, CacheEntry> = new Map();
const listeners = new Set<() => void>();
const pending = new Set<string>();
const inFlight = new Set<string>();
let timer: ReturnType<typeof setTimeout> | null = null;
let disabled = false;

function publish(next: ReadonlyMap<string, CacheEntry>): void {
  cache = next;
  for (const l of listeners) l();
}

async function flush(): Promise<void> {
  timer = null;
  while (pending.size && !disabled) {
    const batch = CoreLinkCards.nextBatch(pending);
    for (const url of batch) {
      pending.delete(url);
      inFlight.add(url);
    }
    let result;
    try {
      result = await data.call('coreLinkCards', batch);
    } catch {
      result = { status: 'failed' as const };
    }
    for (const url of batch) inFlight.delete(url);
    if (result.status === 'ok') {
      publish(CoreLinkCards.merge(cache, { cards: new Map(Object.entries(result.cards)), ttlSeconds: result.ttlSeconds }, Date.now()));
    } else if (result.status === 'unauthorized' || result.status === 'unavailable') {
      disabled = true;
      pending.clear();
    }
    // `failed`: the last cards stay; the next draw may ask again.
  }
}

/** The row with this URL is on screen: ask if the cache says so. */
export function requestCoreCard(url: string): void {
  if (!CoreLinkCards.shouldAsk(cache.get(url), Date.now(), { inFlight: inFlight.has(url) || pending.has(url), disabled })) return;
  pending.add(url);
  if (!timer) timer = setTimeout(() => void flush(), CORE_DEBOUNCE_MS);
}

/**
 * The card for a message's Core link: `undefined` while nothing is known (draw nothing extra),
 * `null` = no card for this reader, else the card. `ref` goes on an element of the row so being
 * drawn (scrolled into view) is what asks.
 */
export function useCoreCard(url: string | null): { card: LinkCard | null | undefined; ref: RefObject<HTMLSpanElement | null> } {
  const ref = useRef<HTMLSpanElement | null>(null);
  const entry = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => (url ? cache.get(url) : undefined),
  );
  useEffect(() => {
    const el = ref.current;
    if (!url || !el) return;
    if (typeof IntersectionObserver === 'undefined') {
      requestCoreCard(url);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) requestCoreCard(url);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [url]);
  return { card: entry ? entry.card : undefined, ref };
}

/** Verification hook. */
export function coreCardsDebug(): { cached: number; disabled: boolean } {
  return { cached: cache.size, disabled };
}
